import 'reflect-metadata';
import { container } from 'tsyringe';
import { RestService } from './RestService';
import { WebSocketService } from './WebSocketService';
import { TransportService, TransportType } from './TransportService';

export type GameTransportConfig = {
    gameName: string;
    serverPort?: number;
    serverProtocol?: 'http' | 'https';
    transportType?: TransportType;
    transportTimeout?: number;
    /**
     * When true (default), REST base is `/api/game/{gameName}/` (atemoya-app / launcher).
     * When false, REST base is `/api/game/` (runtime_dev_app math server).
     */
    gameScopedApiPath?: boolean;
};

function browserHostname(): string {
    if (typeof window !== 'undefined' && window.location?.hostname) {
        return window.location.hostname;
    }
    return 'localhost';
}

/** Create and wire TransportService for a game session (launcher-owned). */
export function createGameTransport(config: GameTransportConfig): TransportService {
    const host = browserHostname();
    const serverPort = config.serverPort ?? 443;
    const serverProtocol = config.serverProtocol ?? 'https';
    const websocketProtocol: 'ws' | 'wss' = serverProtocol === 'https' ? 'wss' : 'ws';
    const gameName = config.gameName;
    const useGameScopedPath = config.gameScopedApiPath !== false;
    const gameSuffix = useGameScopedPath && gameName ? `${gameName}/` : '';
    const restUrl = `${serverProtocol}://${host}:${serverPort}/api/game/${gameSuffix}`;
    const wsUrl = `${websocketProtocol}://${host}:${serverPort}/ws`;

    const child = container.createChildContainer();

    child.register<TransportType>('TRANSPORT_TYPE', {
        useValue: config.transportType ?? TransportType.REST,
    });
    child.register<number>('TRANSPORT_TIMEOUT', {
        useValue: config.transportTimeout ?? 30000,
    });
    child.register<string>('WS_URL', { useValue: wsUrl });
    child.register<string>('REST_URL', { useValue: restUrl });
    child.register<string>('GAME_NAME', { useValue: gameName });

    if (typeof window !== 'undefined') {
        window.GAME_NAME = gameName;
    }

    child.register(RestService, { useClass: RestService });
    child.register(WebSocketService, { useClass: WebSocketService });
    child.register(TransportService, { useClass: TransportService });

    return child.resolve(TransportService);
}
